import type {Router} from 'vue-router'
import type {Ref} from 'vue'
import {onBeforeUnmount} from 'vue'

export function useOringNavigationGuard(
  router: Router,
  showPopup: Ref<boolean>,
  pagePath: string,
  allowedRoutes: string[] = []
) {
  const pendingRoute = ref<string | null>(null)
  const allowedToLeave = ref(false)

  const removeGuard = router.beforeEach((to, from, next) => {
    if (!from.path.includes(pagePath)) {
      return next()
    }

    if (allowedToLeave.value) {
      allowedToLeave.value = false
      return next()
    }

    const isAllowedRoute = allowedRoutes?.length > 0 && allowedRoutes.some(route => to.path.includes(route))
    if (isAllowedRoute) {
      return next()
    }

    // Don't show popup if navigating to the same page
    if (to.path === from.path) {
      return next(false)
    }

    // Validate the route path before storing
    const fullPath = to.fullPath

    if (fullPath && typeof fullPath === 'string' && !fullPath.includes('undefined')) {
      pendingRoute.value = fullPath
      showPopup.value = true
    } else {
      // eslint-disable-next-line no-console
      console.warn('[NavigationGuard] Invalid route detected:', fullPath)
      pendingRoute.value = null
    }

    next(false)
  })

  onBeforeUnmount(() => {
    removeGuard()
  })

  const confirmLeave = () => {
    allowedToLeave.value = true
    return pendingRoute.value
  }

  return {
    confirmLeave,
    clearPendingRoute: () => {
      pendingRoute.value = null
    }
  }
}
